import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  REJECTION_SECTIONS,
  REJECTION_SECTION_LABELS,
  formatDate,
  formatDateTime,
  formatNetPrice,
  formatTotal,
  missingForApproval,
  type RejectionSection,
} from '@pr-app/shared';
import {
  Badge,
  Body1,
  Body1Strong,
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Image,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Option,
  Radio,
  RadioGroup,
  Spinner,
  Textarea,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { fetchAttachmentBlob, type Attachment } from '../api/attachments';
import { ApiError } from '../api/client';
import {
  getCostCentres,
  getLineOfBusiness,
  getProcurementCategories,
  getProjectCategories,
  getSalesTaxGroups,
  getVendors,
  getWarehouses,
  type DimensionRow,
  type ProcurementCategoryRow,
  type ProjectCategoryRow,
  type SalesTaxGroupRow,
  type VendorRow,
  type WarehouseRow,
} from '../api/masterdata';
import {
  decideRequest,
  getPrFailures,
  getReviewQueue,
  type PrAttemptResult,
  type PrFailure,
  type ReviewQueueRequest,
} from '../api/review';
import { getRequest, type RequestDetail } from '../api/requests';
import { ActionBar } from '../components/ActionBar';
import { ApiErrorMessage } from '../components/ApiErrorMessage';
import { AttachmentPreviewDialog } from '../components/AttachmentPreviewDialog';
import { DataTable, type Column } from '../components/DataTable';
import { EmptyState } from '../components/EmptyState';
import { FilterBar } from '../components/FilterBar';
import { PageHeader } from '../components/PageHeader';
import { Section } from '../components/Section';
import { useResponsiveStyles } from '../hooks/useResponsiveStyles';
import { useRestoreFocusOnClose } from '../hooks/useRestoreFocusOnClose';
import { RADIUS, SPACE, SPACE_X } from '../styles/scale';
import { useLayoutStyles, useSurfaceStyles } from '../styles/surfaces';
import {
  partialWriteHint,
  pendingCreationFailure,
  plainFailureHints,
} from '../lib/prFailureNotice';
import { salesTaxLabel } from '../lib/salesTaxLabel';
import { truncateFileNameMiddle } from '../lib/fileNameDisplay';
import {
  dimensionKey,
  dimensionLabel,
  resolveCategoryLabel,
  resolveDependentLabel,
  warehouseKey,
  warehouseLabel,
} from '../lib/masterDataLabels';
import { useVendorNames, vendorLabel } from '../lib/useVendorNames';
import { computeLineTotal, sumLineTotals } from '../lib/totals';
import { PrCreationFailure } from './PrCreationFailure';

/**
 * SOW §4.6, wireframe 05 (V1/V1a) — every request in PENDING_REVIEW, oldest
 * first, shared across the whole reviewer group (a group inbox, not a
 * personal one). The viewer's own request is listed, never hidden, but its
 * action is disabled — a reviewer can't decide on their own request.
 *
 * Review detail with the decision bar (wireframes V2/V3/V4, #41) lives in the
 * same file, keyed off `selectedId` — a master/detail split within one route
 * rather than a nested router path.
 *
 * ── Design-system pass (#202) ──
 * Both screens are assembled from the shared primitives rather than from this
 * file's own card/heading/toolbar definitions: `PageHeader` (the page's one
 * real `<h1>`), `FilterBar`, `DataTable` (one column definition, table on
 * desktop and cards below 700px), `Section`, `EmptyState` and `ActionBar`.
 * What each replaced is noted where it is used.
 */

type LoadState =
  | { phase: 'loading' }
  | { phase: 'ready'; requests: ReviewQueueRequest[]; failures: PrFailure[] }
  | { phase: 'error'; error: unknown };

/** Which screen the reviewer has open: the queue, a review, or a failed creation (#46). */
type Selection = { kind: 'review' | 'failure'; id: string };

const useStyles = makeStyles({
  /** The queue row's flag for a request D365 refused. Words, not colour alone; the colour is a token. */
  failedNote: { color: tokens.colorPaletteRedForeground1 },
  /** The refused-D365-attempt warning: one block per line, the raw message wrapped, never clipped. */
  failureLine: { display: 'block', marginTop: SPACE.xs },
  failureRaw: { display: 'block', marginTop: SPACE.xs, overflowWrap: 'anywhere' },
  /**
   * `currentColor` rather than a palette token: the dot tracks whatever the
   * badge's own colour resolves to, including plain white in high contrast —
   * the exact technique `StatusBadge`/`AuditActionBadge` already use (#54).
   * 8px is the dot's SIZE, not a gap, so it is a literal here for the same
   * reason it is one in `StatusBadge`.
   */
  waitingDot: {
    width: '8px',
    height: '8px',
    borderRadius: RADIUS.pill,
    backgroundColor: 'currentColor',
  },

  /** V1's Request column: the request number, its title and the line/file counts. */
  identity: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.xs,
    minWidth: '0',
  },

  /** De-emphasis is the neutral ramp — never `opacity`, which high contrast cannot survive. */
  muted: { color: tokens.colorNeutralForeground2 },

  /** V7's failed-creation list. `surfaces.card` draws each row; this only stacks them. */
  failureList: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.s,
    listStyleType: 'none',
    padding: SPACE.none,
    margin: SPACE.none,
  },
  failureRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: SPACE_X.m,
    flexWrap: 'wrap',
  },
  failureIdentity: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACE_X.s,
    flexWrap: 'wrap',
    minWidth: '0',
  },

  /** A stack of blocks inside one `Section` — the section owns the card, this owns its rhythm. */
  block: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.m,
  },

  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: SPACE.m,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.xs,
    minWidth: '0',
  },

  /**
   * Wireframe S1c — "the grand total pins to the bottom of the line list as a
   * distinct surface". One element for both faces of the list, so the table
   * and the mobile cards can never disagree on the figure being approved.
   */
  grandTotal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: SPACE_X.m,
  },
  /** Wireframe X1: money compares by eye only if the figures are the same width. */
  numeric: { fontVariantNumeric: 'tabular-nums' },

  thumbnailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
    gap: SPACE_X.m,
  },
  /**
   * A native `<button>` does NOT inherit `color` from `FluentProvider` — it
   * falls back to the browser's `buttontext` system colour, which collided with
   * pure black and rendered the Dashboard's stat tiles blank in high contrast
   * (CLAUDE.md log entry 54). It survived here only because its one text child
   * happened to set its own colour; the token is explicit now.
   */
  thumbnailButton: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.xs,
    padding: SPACE.none,
    border: 'none',
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground1,
    cursor: 'pointer',
    textAlign: 'start',
  },
  thumbnailImage: {
    width: '100%',
    aspectRatio: '1',
    objectFit: 'cover',
    borderRadius: RADIUS.control,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  thumbnailCaption: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: tokens.colorNeutralForeground2,
  },
  lightboxImage: {
    maxWidth: '100%',
    maxHeight: '70vh',
    display: 'block',
    marginInlineStart: 'auto',
    marginInlineEnd: 'auto',
  },
  documentRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: SPACE_X.m,
  },

  /** The back affordance is a callback, not a route, so it cannot be `PageHeader`'s own `backTo` link. */
  backButton: { alignSelf: 'flex-start' },

  /**
   * Wireframes V5/V6 — a full-screen milestone. Measured rather than fluid: it
   * is one sentence of copy, and a sentence set across the whole tab canvas is
   * unreadable.
   */
  milestone: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.l,
    alignItems: 'center',
    textAlign: 'center',
  },

  dialogField: { marginTop: SPACE.m },
});

function purchaseTypeLabel(purchaseType: string | null): string {
  return purchaseType === 'CAPITAL' ? 'Capital' : 'Regular';
}

/** Wireframe V1's own examples read "2 days", "1 day", "4 hours", "1 hour". */
function formatWaiting(since: string): string {
  const elapsedMs = Date.now() - new Date(since).getTime();
  const minutes = Math.floor(elapsedMs / 60_000);
  if (minutes < 60) return `${String(Math.max(minutes, 1))} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${String(hours)} hour${hours === 1 ? '' : 's'}`;
  const days = Math.floor(hours / 24);
  return `${String(days)} day${days === 1 ? '' : 's'}`;
}

/**
 * The Waiting column's own dot-and-word treatment (wireframe V1) — the same
 * pattern `StatusBadge`/`AuditActionBadge` use, not colour alone: high
 * contrast has no colour at all (sheet 08.1 note 1), so the word is what
 * survives and the outline keeps the pill visible once the palette
 * background collapses to black.
 */
function WaitingBadge({ since }: { since: string }) {
  const styles = useStyles();
  return (
    <Badge
      appearance="outline"
      color="informative"
      size="medium"
      icon={<span aria-hidden="true" className={styles.waitingDot} />}
    >
      {formatWaiting(since)}
    </Badge>
  );
}

function isImage(contentType: string): boolean {
  return contentType.startsWith('image/');
}

function dateLabel(iso: string | null): string {
  // A stored calendar day is UTC midnight with no zone of its own, so read it in UTC — the local
  // zone would show the previous day to anyone west of Greenwich.
  return iso ? formatDate(iso, { timeZone: 'UTC' }) : '';
}

/** Fetches the object URL once per attachment through the authenticated download route. */
function useAttachmentImageUrl(attachment: Attachment): string | undefined {
  const [url, setUrl] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!isImage(attachment.contentType)) return undefined;
    let objectUrl: string | undefined;
    let cancelled = false;
    setUrl(undefined);
    void fetchAttachmentBlob(attachment.id)
      .then((blob) => {
        if (cancelled) return undefined;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
        return undefined;
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment.id, attachment.contentType]);
  return url;
}

function ReviewThumbnail({ attachment, onOpen }: { attachment: Attachment; onOpen: () => void }) {
  const styles = useStyles();
  const url = useAttachmentImageUrl(attachment);
  return (
    <button
      type="button"
      className={styles.thumbnailButton}
      onClick={onOpen}
      aria-label={`Open ${attachment.fileName}`}
    >
      {url ? (
        <Image src={url} alt="" className={styles.thumbnailImage} />
      ) : (
        <div className={styles.thumbnailImage}>
          <Spinner size="tiny" />
        </div>
      )}
      {/* X2 note 1: a filename truncates in the MIDDLE, so the extension —
          the part that says what the file is — stays visible. */}
      <Caption1 className={styles.thumbnailCaption} title={attachment.fileName}>
        {truncateFileNameMiddle(attachment.fileName)}
      </Caption1>
    </button>
  );
}

type DetailState =
  | { phase: 'loading' }
  | { phase: 'ready'; request: RequestDetail }
  | { phase: 'error'; error: unknown };

/**
 * #147 — the reviewer detail screen must resolve `procurementCategoryId`/
 * `costCentreId`/`lineOfBusinessId`/`warehouseId`/`vendorAccount` to the same
 * display labels the requester saw (`RequestForm.tsx`'s `categoryLabel`/
 * `dimensionLabel`/`resolveDependentLabel`), not print the raw D365 code.
 * Each list resolves independently — `phase: 'loading'` until its own fetch
 * settles — so a slow or unreachable one only degrades its own field(s) back
 * to the raw code (`resolveDependentLabel`'s own fallback), never the whole
 * screen.
 */
type MasterList<T> = { phase: 'loading' } | { phase: 'ready'; rows: T[] } | { phase: 'error' };

interface ReviewMasterData {
  categories: MasterList<ProcurementCategoryRow>;
  costCentres: MasterList<DimensionRow>;
  lineOfBusiness: MasterList<DimensionRow>;
  warehouses: MasterList<WarehouseRow>;
  vendors: MasterList<VendorRow>;
  salesTaxGroups: MasterList<SalesTaxGroupRow>;
  projectCategories: MasterList<ProjectCategoryRow>;
}

const LOADING_MASTER_DATA: ReviewMasterData = {
  categories: { phase: 'loading' },
  costCentres: { phase: 'loading' },
  lineOfBusiness: { phase: 'loading' },
  warehouses: { phase: 'loading' },
  vendors: { phase: 'loading' },
  salesTaxGroups: { phase: 'loading' },
  projectCategories: { phase: 'loading' },
};

/**
 * Fetches the master-data lists needed to resolve this request's codes to
 * labels — categories once, the three company-dependent dimension lists and
 * the vendor once the request (and so its company code / vendor account) has
 * loaded. Never persisted or reused across requests (Hard Rule: master data
 * is always fetched live from D365, never cached locally).
 */
function useReviewMasterData(request: RequestDetail | null): ReviewMasterData {
  const [masterData, setMasterData] = useState<ReviewMasterData>(LOADING_MASTER_DATA);

  useEffect(() => {
    if (!request) {
      setMasterData(LOADING_MASTER_DATA);
      return undefined;
    }
    let cancelled = false;
    setMasterData(LOADING_MASTER_DATA);

    const company = request.companyCode;
    const vendorAccount = request.vendorAccount;

    void Promise.allSettled([
      getProcurementCategories(),
      company ? getCostCentres(company) : Promise.resolve<DimensionRow[]>([]),
      company ? getLineOfBusiness(company) : Promise.resolve<DimensionRow[]>([]),
      company ? getWarehouses(company) : Promise.resolve<WarehouseRow[]>([]),
      vendorAccount ? getVendors(vendorAccount, 1, company) : Promise.resolve<VendorRow[]>([]),
      company ? getSalesTaxGroups(company) : Promise.resolve<SalesTaxGroupRow[]>([]),
      company && request.projectId
        ? getProjectCategories(company)
        : Promise.resolve<ProjectCategoryRow[]>([]),
    ]).then(
      ([
        categories,
        costCentres,
        lineOfBusiness,
        warehouses,
        vendors,
        salesTaxGroups,
        projectCategories,
      ]) => {
        if (cancelled) return;
        const toList = <T,>(result: PromiseSettledResult<T[]>): MasterList<T> =>
          result.status === 'fulfilled'
            ? { phase: 'ready', rows: result.value }
            : { phase: 'error' };
        setMasterData({
          categories: toList(categories),
          costCentres: toList(costCentres),
          lineOfBusiness: toList(lineOfBusiness),
          warehouses: toList(warehouses),
          vendors: toList(vendors),
          salesTaxGroups: toList(salesTaxGroups),
          projectCategories: toList(projectCategories),
        });
      },
    );

    return () => {
      cancelled = true;
    };
  }, [request]);

  return masterData;
}

function vendorNameLabel(row: VendorRow): string {
  return row.name;
}

/**
 * Decimal-safe (never a JS float, per CLAUDE.md's "Money fields: string/Decimal
 * only") — the same `computeLineTotal` helper `RequestForm.tsx` uses, so the
 * requester's and reviewer's screens can never disagree on a line's total.
 */
function lineTotalLabel(line: RequestDetail['lines'][number], currencyCode: string): string {
  const total = computeLineTotal(line.quantity, line.netPrice);
  return total ? formatTotal(total, currencyCode) : '';
}

function buildDetailFields(
  request: RequestDetail,
  masterData: ReviewMasterData,
): { label: string; value: string }[] {
  return [
    { label: 'Type of purchase', value: purchaseTypeLabel(request.purchaseType) },
    { label: 'Company', value: request.companyCode ?? '' },
    // The account number is its own label, so no extra D365 lookup on the review screen.
    ...(request.mainAccountRequired === false
      ? []
      : [{ label: 'Main account', value: request.mainAccountId ?? '' }]),
    {
      label: 'Cost centre',
      value: resolveDependentLabel(
        masterData.costCentres,
        request.costCentreId,
        dimensionKey,
        dimensionLabel,
      ),
    },
    {
      label: 'Line of business',
      value: resolveDependentLabel(
        masterData.lineOfBusiness,
        request.lineOfBusinessId,
        dimensionKey,
        dimensionLabel,
      ),
    },
    {
      label: 'Warehouse',
      value: resolveDependentLabel(
        masterData.warehouses,
        request.warehouseId,
        warehouseKey,
        warehouseLabel,
      ),
    },
    {
      label: 'Vendor',
      value: resolveDependentLabel(
        masterData.vendors,
        request.vendorAccount,
        (row) => row.accountNumber,
        vendorNameLabel,
      ),
    },
    { label: 'Delivery date', value: dateLabel(request.deliveryDate) },
    {
      label: 'Project',
      value:
        request.purchaseType === 'CAPITAL' ? (request.projectId ?? '') : 'Not applicable — Regular',
    },
    ...(request.purchaseType === 'CAPITAL'
      ? [
          {
            label: 'Project category',
            value: salesTaxLabel(masterData.projectCategories, request.projectCategoryId),
          },
        ]
      : []),
    { label: 'Internal notes', value: request.internalNotes ?? '' },
    { label: 'Notes for vendor', value: request.notesForVendor ?? '' },
  ];
}

/** Category resolved to a label the same way `RequestForm.tsx`'s own line rows show it. */
function categoryFieldLabel(
  line: RequestDetail['lines'][number],
  categories: MasterList<ProcurementCategoryRow>,
): string {
  return resolveCategoryLabel(categories, line.procurementCategoryId);
}

/** A line plus its position in the list — the mobile card's lead line ("Line 2"). */
type LineRow = RequestDetail['lines'][number] & { displayNo: number };

/**
 * Wireframes V2/V3/V4 (#41) — read-only mirror of the requester's own R5
 * review screen, same data in the same order.
 *
 * ── Layout (#209) ──
 * `useLayoutStyles().fluid`: the request fills the whole tab canvas — same
 * treatment #206 gave `RequestForm.tsx` — rather than being capped at a
 * measured reading width with the decision panel in a companion rail beside
 * it. Reject/Approve now live in a full-width `ActionBar` at the bottom of
 * the screen, the same footer convention every `RequestForm.tsx` step uses
 * (Back/Save & close/Next), not a floating side box.
 *
 * K1 still holds: the decision bar is the LAST thing in DOM order (dialogs
 * aside), so a keyboard user reads the whole request before reaching
 * Approve — it is simply a sibling block now instead of a grid item.
 */
function ReviewDetail({
  requestId,
  onBack,
  onDecided,
}: {
  requestId: string;
  onBack: () => void;
  onDecided: () => void;
}) {
  const styles = useStyles();
  const layout = useLayoutStyles();
  const surfaces = useSurfaceStyles();
  const responsive = useResponsiveStyles();
  const [state, setState] = useState<DetailState>({ phase: 'loading' });
  const navigate = useNavigate();
  // #260 — one shared preview dialog for every attachment type; the index is
  // into `request.attachments` (the FULL list), not just the image subset,
  // so Previous/Next in the dialog can move across every type.
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [dialog, setDialog] = useState<'approve' | 'reject' | null>(null);
  const [comment, setComment] = useState('');
  // #155, wireframe V3 — which section of the request the rejection is
  // about, optional. Sent alongside the mandatory comment and read back on
  // the requester's resubmit screen (R8) as a badge.
  const [rejectSection, setRejectSection] = useState<RejectionSection | ''>('');
  // #153 — the approve dialog's own optional comment (V4), separate from
  // reject's mandatory one above so switching dialogs never leaks one
  // decision's note into the other's textbox.
  const [approveComment, setApproveComment] = useState('');
  const [deciding, setDeciding] = useState(false);
  const [decideError, setDecideError] = useState<string | null>(null);
  // #153, wireframes V5/V6 — 'creating' replaces the whole screen with a
  // loading state while the D365 attempt an approval triggers actually runs,
  // and 'created' shows a brief confirmation naming the PR before returning
  // to the queue. A refused attempt (`prCreation.outcome === 'FAILED'`) skips
  // 'created' — it goes straight back, same as before (#46): the approval
  // stands and the failure is findable from the queue's own failures list.
  const [approvePhase, setApprovePhase] = useState<'idle' | 'creating' | 'created'>('idle');
  const [prCreationResult, setPrCreationResult] = useState<PrAttemptResult | null>(null);

  // K1 "Dialog opens": "Reject → the comment box. Approve → the confirm
  // button." Fluent focuses the *first* focusable element in the surface,
  // which happens to be right for Reject (the textarea) but lands on Cancel
  // for Approve — so both are placed explicitly rather than one relying on a
  // default that only coincidentally agrees with the spec.
  const rejectCommentRef = useRef<HTMLTextAreaElement>(null);
  const confirmApproveRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (dialog === 'reject') rejectCommentRef.current?.focus();
    if (dialog === 'approve') confirmApproveRef.current?.focus();
  }, [dialog]);

  // K1 "Dialog closes": back to Reject/Approve on the decision bar.
  useRestoreFocusOnClose(dialog !== null);
  useRestoreFocusOnClose(previewIndex !== null);

  // #147 — fetched regardless of `state.phase` (hooks can't be conditional);
  // resolves to nothing until the request itself has loaded.
  const masterData = useReviewMasterData(state.phase === 'ready' ? state.request : null);

  const load = useCallback(async () => {
    setState({ phase: 'loading' });
    try {
      const request = await getRequest(requestId);
      setState({ phase: 'ready', request });
    } catch (err) {
      setState({ phase: 'error', error: err });
    }
  }, [requestId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function submitDecision(decision: 'APPROVE' | 'REJECT') {
    setDeciding(true);
    setDecideError(null);
    // Wireframe V5 — close the confirm dialog and switch to the full-screen
    // "Creating…" state before awaiting, rather than leaving the dialog open
    // with a small spinner: this is a real, sometimes-slow D365 write, and
    // the reviewer should see that clearly, not guess from a static dialog.
    // Reject has no D365 step, so it keeps the original in-dialog spinner.
    if (decision === 'APPROVE') {
      setDialog(null);
      setApprovePhase('creating');
    }
    try {
      const decisionComment = decision === 'APPROVE' ? approveComment : comment;
      const result = await decideRequest(
        requestId,
        decision,
        decisionComment.trim() ? decisionComment : undefined,
        decision === 'REJECT' && rejectSection ? rejectSection : undefined,
      );
      setDialog(null);
      setComment('');
      setApproveComment('');
      setRejectSection('');
      if (decision === 'REJECT') {
        onDecided();
        return;
      }
      // Approve: a created PR gets the V6 confirmation naming it. A refused attempt leaves the
      // request pending review (it is not approved until D365 has it), so the reviewer stays on
      // it — reloaded, the warning at the top says what D365 said, and Edit request and Approve
      // are right there to fix it and try again.
      if (result.prCreation?.outcome === 'CREATED') {
        setPrCreationResult(result.prCreation);
        setApprovePhase('created');
      } else {
        setApprovePhase('idle');
        await load();
      }
    } catch (err) {
      setApprovePhase('idle');
      setDecideError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeciding(false);
    }
  }

  /**
   * Deliberately NOT a `PageHeader` on the loading and error states: the page
   * title here is the request's own number, which is not known until the fetch
   * lands, and an `<h1>` naming a request that may not exist is worse than no
   * heading at all.
   */
  const backButton = (
    <Button appearance="transparent" className={styles.backButton} onClick={onBack}>
      ← Queue
    </Button>
  );

  if (state.phase === 'loading') {
    return (
      <div className={layout.stack}>
        {backButton}
        {/* Two-second rule: this is a network call on the render path. */}
        <Spinner label="Loading the request…" labelPosition="after" />
      </div>
    );
  }

  if (state.phase === 'error') {
    return (
      <div className={layout.stack}>
        {backButton}
        <ApiErrorMessage
          error={state.error}
          title="Could not load this request"
          onRetry={() => void load()}
        />
      </div>
    );
  }

  const { request } = state;
  // What the requester left empty. A reviewer may not approve until these are
  // filled in (the server enforces the same rule); "Edit request" is the way to.
  const missing = missingForApproval(request, {
    mainAccountRequired: request.mainAccountRequired,
  });
  // A refused D365 attempt leaves the request here, still awaiting a decision.
  const creationFailure = pendingCreationFailure(request.status, request.auditLog);

  // Wireframe V5 — the D365 write an approval triggers is genuinely slow
  // (CLAUDE.md's "two-second rule" for anything network-bound on the render
  // path); this replaces the whole screen rather than a small in-dialog
  // spinner, because there is no meaningful "back" while the request is
  // mid-approval.
  if (approvePhase === 'creating') {
    return (
      <div className={mergeClasses(layout.measured, styles.milestone, responsive.touchTargets)}>
        <Spinner
          label={`Creating the Purchase Requisition for PR-${String(request.requestNo)} in D365…`}
          labelPosition="below"
        />
      </div>
    );
  }

  // Wireframe V6 — a brief, explicit confirmation naming the created PR
  // before returning to the queue, mirroring the full-screen milestone
  // pattern `RequestForm.tsx`'s own "Submitted for review" confirmation
  // uses (a toast would be gone before it was read).
  if (approvePhase === 'created' && prCreationResult?.outcome === 'CREATED') {
    const backToQueue = () => {
      setApprovePhase('idle');
      setPrCreationResult(null);
      onDecided();
    };

    return (
      <div className={mergeClasses(layout.stack, layout.measured, responsive.touchTargets)}>
        <PageHeader title="Purchase Requisition created" />
        <Body1 as="p">
          PR-{request.requestNo} was approved and Purchase Requisition {prCreationResult.prNumber}{' '}
          was created in D365.
        </Body1>
        {prCreationResult.attachmentFailure !== null && (
          <Body1 as="p" className={styles.muted}>
            Its attachments could not be linked in D365 — see the request’s own history for what
            D365 said.
          </Body1>
        )}
        <ActionBar primary={{ label: 'Back to queue', onClick: backToQueue }} />
      </div>
    );
  }

  const images = request.attachments.filter((a) => isImage(a.contentType));
  const documents = request.attachments.filter((a) => !isImage(a.contentType));
  // #147 — Decimal-safe, via the same helper `RequestForm.tsx` uses for its own
  // grand total, so the two screens can never disagree on the figure being approved.
  // #148 — kept as a Decimal (not pre-rounded to a string) so it can be grouped
  // for the request's own currency wherever it's displayed below.
  const grandTotal = sumLineTotals(
    request.lines.map((line) => computeLineTotal(line.quantity, line.netPrice)),
  );
  const currencyCode = request.currencyCode ?? '';
  const grandTotalLabel = formatTotal(grandTotal, currencyCode);

  const lineRows: LineRow[] = request.lines.map((line, index) => ({
    ...line,
    displayNo: index + 1,
  }));

  /**
   * ONE definition, two faces — the seven columns become one labelled card per
   * line below 700px rather than a sideways scroll (Teams forbids one), and
   * neither face can gain or lose a field the other has. `priority` orders the
   * mobile card so the top of it answers "what, and how much".
   */
  const lineColumns: Column<LineRow>[] = [
    {
      key: 'item',
      header: 'Item',
      priority: 100,
      render: (line) => line.itemDescription || '—',
    },
    {
      key: 'category',
      header: 'Category',
      priority: 50,
      render: (line) => categoryFieldLabel(line, masterData.categories) || '—',
    },
    {
      key: 'quantity',
      header: 'Quantity',
      numeric: true,
      priority: 80,
      render: (l) => l.quantity || '—',
    },
    { key: 'unit', header: 'Unit', priority: 40, render: (line) => line.unitOfMeasure || '—' },
    {
      key: 'delivery',
      header: 'Delivery',
      priority: 30,
      render: (line) => dateLabel(line.deliveryDate) || '—',
    },
    {
      key: 'netPrice',
      header: 'Unit price',
      numeric: true,
      priority: 70,
      render: (line) => (line.netPrice ? formatNetPrice(line.netPrice, currencyCode) : '—'),
    },
    {
      key: 'startDate',
      header: 'Start date',
      priority: 20,
      render: (line) => dateLabel(line.startDate) || '—',
    },
    {
      key: 'endDate',
      header: 'End date',
      priority: 20,
      render: (line) => dateLabel(line.endDate) || '—',
    },
    {
      key: 'salesTax',
      header: 'Sales tax',
      priority: 20,
      render: (line) => salesTaxLabel(masterData.salesTaxGroups, line.salesTaxGroupId) || '—',
    },
    {
      key: 'lineTotal',
      header: 'Line total',
      numeric: true,
      priority: 90,
      render: (line) => lineTotalLabel(line, currencyCode) || '—',
    },
  ];

  return (
    <div className={mergeClasses(layout.stack, responsive.touchTargets)}>
      {backButton}
      <PageHeader title={`PR-${String(request.requestNo)}`} />

      {/* At the TOP, not beside the buttons: the decision bar is sticky at the foot of the
          viewport but this page is long, so a warning placed just above that bar sits at the
          very end of the content — the reviewer would see a greyed-out Approve and no reason
          until they scrolled all the way down. */}
      {creationFailure && (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>
              Not approved yet — D365 could not create the requisition
            </MessageBarTitle>
            <span className={styles.failureLine}>
              The request is still pending review. Correct it (Edit request) and approve again — the
              retry continues the same requisition and never makes a second one — or reject it back
              to the requester.
            </span>
            {plainFailureHints(creationFailure.message, 'approve again').map((hint) => (
              <span key={hint} className={styles.failureLine}>
                {hint}
              </span>
            ))}
            {partialWriteHint(creationFailure.step) && (
              <span className={styles.failureLine}>{partialWriteHint(creationFailure.step)}</span>
            )}
            <span className={styles.failureLine}>D365 returned:</span>
            <code className={styles.failureRaw}>{creationFailure.message}</code>
          </MessageBarBody>
        </MessageBar>
      )}

      {missing.length > 0 && (
        <MessageBar intent="warning">
          <MessageBarBody>
            <MessageBarTitle>Fill in before approving</MessageBarTitle>
            {missing.map((item) => item.label).join(', ')}. Use Edit request to add them.
          </MessageBarBody>
        </MessageBar>
      )}

      <div className={layout.fluid}>
        <div className={layout.stack}>
          <Section title="Requester">
            <div className={styles.field}>
              <Body1Strong>{request.requesterName}</Body1Strong>
              <Body1>{request.requesterEmail}</Body1>
            </div>
          </Section>

          <Section title="Details">
            <div className={mergeClasses(styles.detailsGrid, responsive.singleColumn)}>
              {buildDetailFields(request, masterData).map((field) => (
                <div key={field.label} className={styles.field}>
                  <Caption1 className={styles.muted}>{field.label}</Caption1>
                  <Body1Strong>{field.value || '—'}</Body1Strong>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Line items" count={request.lines.length}>
            <div className={styles.block}>
              <DataTable
                caption="Line items"
                columns={lineColumns}
                rows={lineRows}
                rowKey={(line) => line.id}
                cardTitle={(line) => `Line ${String(line.displayNo)}`}
              />
              {/* Sheet 08 S1c — the grand total is not a table row: it belongs
                  to the whole list, so it is a distinct surface pinned below it
                  in BOTH faces rather than a `<tfoot>` the cards would lose. */}
              <div className={mergeClasses(surfaces.inset, styles.grandTotal)}>
                <Body1Strong>Grand total</Body1Strong>
                <Body1Strong className={styles.numeric}>
                  {grandTotalLabel} {currencyCode}
                </Body1Strong>
              </div>
            </div>
          </Section>

          <Section title="Attachments" count={request.attachments.length}>
            {request.attachments.length === 0 ? (
              <Body1 as="p" className={styles.muted}>
                No files attached.
              </Body1>
            ) : (
              <div className={styles.block}>
                {images.length > 0 && (
                  <div className={styles.thumbnailGrid}>
                    {images.map((attachment) => (
                      <ReviewThumbnail
                        key={attachment.id}
                        attachment={attachment}
                        onOpen={() => setPreviewIndex(request.attachments.indexOf(attachment))}
                      />
                    ))}
                  </div>
                )}
                {documents.map((attachment) => (
                  <div
                    key={attachment.id}
                    className={mergeClasses(surfaces.inset, styles.documentRow)}
                  >
                    <Body1 as="span" title={attachment.fileName}>
                      {truncateFileNameMiddle(attachment.fileName)}
                    </Body1>
                    <Button
                      appearance="secondary"
                      size="small"
                      onClick={() => setPreviewIndex(request.attachments.indexOf(attachment))}
                    >
                      Open
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      </div>

      {decideError && (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Could not record the decision</MessageBarTitle>
            {decideError}
          </MessageBarBody>
        </MessageBar>
      )}
      {/* The decision bar — K1: LAST thing in DOM order (dialogs aside), so a
          keyboard user reaches Approve only after reading the whole request.
          `sticky` brings E2 (`surfaces.stickyBar`): without its own shadow the
          bar shares `colorNeutralBackground1` with every card and, scrolled
          over a subsection, reads as part of that card. Full-width at the
          bottom of the screen, same convention `RequestForm.tsx`'s own step
          footers use — Reject as the secondary/outline action, Approve as the
          primary one. */}
      <ActionBar
        sticky
        secondary={
          <>
            <Button appearance="secondary" onClick={() => setDialog('reject')}>
              Reject
            </Button>
            <Button
              appearance="secondary"
              onClick={() => void navigate(`/review/${request.id}/edit`)}
            >
              Edit request
            </Button>
          </>
        }
        primary={{
          label: 'Approve',
          onClick: () => setDialog('approve'),
          disabled: missing.length > 0,
        }}
      />

      {previewIndex !== null && (
        <AttachmentPreviewDialog
          attachments={request.attachments}
          initialIndex={previewIndex}
          onClose={() => setPreviewIndex(null)}
        />
      )}

      {dialog && (
        <Dialog open onOpenChange={(_, data) => !data.open && setDialog(null)}>
          <DialogSurface className={responsive.touchTargets}>
            <DialogBody>
              {dialog === 'approve' ? (
                <>
                  <DialogTitle>Approve PR-{request.requestNo}?</DialogTitle>
                  <DialogContent>
                    Approving creates a Purchase Requisition in D365 FO and cannot be undone{' '}
                    <Body1Strong>from this app</Body1Strong> — it can still be corrected in D365
                    itself. The requester is not notified until the PR is created there.
                    <Field label="Comment (optional)" className={styles.dialogField}>
                      <Textarea
                        value={approveComment}
                        onChange={(_, data) => setApproveComment(data.value)}
                        placeholder="Add a note for the audit trail"
                      />
                    </Field>
                  </DialogContent>
                </>
              ) : (
                <>
                  <DialogTitle>Reject PR-{request.requestNo}?</DialogTitle>
                  <DialogContent>
                    <Field label="Reason for rejection" required>
                      <Textarea
                        ref={rejectCommentRef}
                        value={comment}
                        onChange={(_, data) => setComment(data.value)}
                        placeholder="Explain what needs to change before this can be resubmitted"
                      />
                    </Field>
                    {/* #155, wireframe V3 — optional: names which section the requester
                        should look at first (R8's badge on resubmit). */}
                    <Field label="Section (optional)" className={styles.dialogField}>
                      <RadioGroup
                        layout="horizontal"
                        value={rejectSection}
                        onChange={(_, data) => setRejectSection(data.value as RejectionSection)}
                      >
                        {REJECTION_SECTIONS.map((section) => (
                          <Radio
                            key={section}
                            value={section}
                            label={REJECTION_SECTION_LABELS[section]}
                          />
                        ))}
                      </RadioGroup>
                    </Field>
                  </DialogContent>
                </>
              )}
              <DialogActions>
                <Button appearance="secondary" onClick={() => setDialog(null)} disabled={deciding}>
                  Cancel
                </Button>
                {dialog === 'approve' ? (
                  <Button
                    ref={confirmApproveRef}
                    appearance="primary"
                    disabled={deciding}
                    onClick={() => void submitDecision('APPROVE')}
                  >
                    {deciding ? <Spinner size="tiny" /> : 'Approve & create PR'}
                  </Button>
                ) : (
                  <Button
                    appearance="primary"
                    disabled={deciding || comment.trim().length === 0}
                    onClick={() => void submitDecision('REJECT')}
                  >
                    {deciding ? <Spinner size="tiny" /> : 'Reject request'}
                  </Button>
                )}
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
    </div>
  );
}

/** Wireframe R1c's own debounce, applied to V1's search box too (#152's precedent). */
const SEARCH_DEBOUNCE_MS = 300;

/** Search box + company filter (V1) — both optional, applied server-side. */
interface QueueFilters {
  q: string;
  company: string;
}

const DEFAULT_QUEUE_FILTERS: QueueFilters = { q: '', company: '' };

/**
 * The queue's per-row action. `secondary`, never `primary`: one emphasised
 * button per SCREEN, not one per row — twenty filled indigo buttons down a
 * table is twenty things claiming to be the next step (#202).
 */
function ReviewButton({
  request,
  onSelect,
}: {
  request: ReviewQueueRequest;
  onSelect: () => void;
}) {
  return (
    <Button
      appearance="secondary"
      disabled={request.isOwn}
      title={request.isOwn ? 'You cannot review your own request' : undefined}
      aria-label={
        request.isOwn
          ? `Review PR-${request.requestNo} — you cannot review your own request`
          : `Review PR-${request.requestNo}`
      }
      onClick={onSelect}
    >
      Review
    </Button>
  );
}

export function ReviewerQueue() {
  const styles = useStyles();
  const layout = useLayoutStyles();
  const surfaces = useSurfaceStyles();
  const responsive = useResponsiveStyles();
  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  // A refetch is a BUSY state, not a loading one — wireframe R1 note 3: "it
  // never blanks the table; existing rows stay until results land". `FilterBar`
  // shows the progress beside its own controls, so a keystroke never unmounts
  // the input the reviewer is typing into.
  const [busy, setBusy] = useState(false);
  // #222 — the Dashboard's own "Review" action (mode="review") navigates here
  // as `/review?open=<id>` to open a specific request's detail directly,
  // rather than duplicating this screen's approve/reject UI. Read once, on
  // mount, the same way any other initial-state-from-the-URL value would be —
  // a later navigation to `/review` with no `open` param (the tab strip's own
  // click) starts fresh, since `App.tsx`'s `navigate('/review')` carries none.
  const [searchParams] = useSearchParams();
  const [selected, setSelected] = useState<Selection | null>(() => {
    const openId = searchParams.get('open');
    return openId ? { kind: 'review', id: openId } : null;
  });
  const [filters, setFilters] = useState<QueueFilters>(DEFAULT_QUEUE_FILTERS);
  const [searchInput, setSearchInput] = useState('');
  // Wireframe V1's "Company: All" lists every company across the whole
  // queue, not just whatever the current search/filter happens to show —
  // captured from the most recent *unfiltered* load, same rationale as the
  // Dashboard's own `companies` (wireframe R1 note 2), computed client-side
  // here since the queue's own `ReviewQueueRequest[]` return type carries no
  // separate field for it.
  const [companies, setCompanies] = useState<string[]>([]);

  // Search box — debounced ~300ms, server-side, mirroring Dashboard.tsx's own
  // search field (#152). Typing never blanks the table; the previous result
  // stays until the new one lands.
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((f) => (f.q === searchInput ? f : { ...f, q: searchInput }));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const load = useCallback(async (params: QueueFilters, signal?: AbortSignal) => {
    setBusy(true);
    // Only the FIRST load (or one after a failure) has nothing to show, so only
    // that one takes the whole screen down to a spinner.
    setState((previous) => (previous.phase === 'ready' ? previous : { phase: 'loading' }));
    try {
      // Both in one round trip's worth of waiting: a failed creation (#46) is
      // reviewer work exactly like a pending request, and hiding it behind a
      // second load would be the same "nobody finds out" the screen exists to
      // prevent.
      const [requests, failures] = await Promise.all([
        getReviewQueue({ q: params.q || undefined, company: params.company || undefined }),
        getPrFailures(),
      ]);
      if (signal?.aborted) return;
      setState({ phase: 'ready', requests, failures });
      if (!params.q && !params.company) {
        setCompanies([
          ...new Set(requests.map((r) => r.companyCode).filter((c): c is string => c !== null)),
        ]);
      }
    } catch (err) {
      if (signal?.aborted) return;
      setState({ phase: 'error', error: err });
    } finally {
      if (!signal?.aborted) setBusy(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(filters, controller.signal);
    return () => controller.abort();
  }, [load, filters]);

  const reload = useCallback(() => load(filters), [load, filters]);

  function clearFilters() {
    setSearchInput('');
    setFilters({ ...DEFAULT_QUEUE_FILTERS });
  }

  const vendorNames = useVendorNames(state.phase === 'ready' ? state.requests : undefined);

  if (selected?.kind === 'review') {
    return (
      <ReviewDetail
        requestId={selected.id}
        onBack={() => setSelected(null)}
        onDecided={() => {
          // Back to the queue either way — if the approval's D365 step failed,
          // the request is now in the "could not be created" section below
          // rather than gone.
          setSelected(null);
          void reload();
        }}
      />
    );
  }

  if (selected?.kind === 'failure') {
    return (
      <PrCreationFailure
        requestId={selected.id}
        onBack={() => setSelected(null)}
        onResolved={() => {
          setSelected(null);
          void reload();
        }}
      />
    );
  }

  const requests = state.phase === 'ready' ? state.requests : [];
  const failures = state.phase === 'ready' ? state.failures : [];
  const actionableCount = requests.filter((r) => !r.isOwn).length;
  const ownCount = requests.length - actionableCount;
  const activeFilterCount = (filters.q ? 1 : 0) + (filters.company ? 1 : 0);
  const filtersActive = activeFilterCount > 0;
  // V1a — a genuinely empty queue (no filters applied either) gets the plain
  // success screen, no search box at all. A queue emptied *by* a filter gets
  // the E1-style "no matches" screen instead, with a way to clear back.
  const showFilters = state.phase === 'ready' && (requests.length > 0 || filtersActive);
  const hasNoMatches = state.phase === 'ready' && requests.length === 0 && filtersActive;
  const genuinelyEmpty = state.phase === 'ready' && requests.length === 0 && !filtersActive;
  // V1b — the reviewer group is a D365/Entra membership, so "you are not in it"
  // is a normal answer, not a fault: it gets the `no-permission` empty state
  // rather than the E3 "something went wrong on our side" error, which would
  // send the viewer to retry a call that will always answer the same way.
  const notAReviewer =
    state.phase === 'error' && state.error instanceof ApiError && state.error.status === 403;

  /** Named rather than inlined so it reads as a render prop, not a component defined mid-render. */
  const renderRowActions = (request: ReviewQueueRequest) => (
    <ReviewButton
      request={request}
      onSelect={() => setSelected({ kind: 'review', id: request.id })}
    />
  );

  /**
   * V1's columns, declared ONCE: a table at desktop width, one labelled card
   * per request below 700px (#53 kept the queue a card list; `DataTable` is now
   * what guarantees the two faces carry the same fields).
   */
  const columns: Column<ReviewQueueRequest>[] = [
    {
      key: 'request',
      header: 'Request',
      priority: 100,
      // Desktop only: the mobile card is already headed "PR-150", so rendering
      // this column there would print the request number twice. The card gets
      // the same three facts from `summary` below, without the repetition.
      tableOnly: true,
      render: (request) => (
        <div className={styles.identity}>
          <Body1Strong>PR-{request.requestNo}</Body1Strong>
          <Body1>{request.title}</Body1>
          <Caption1 className={styles.muted}>
            {request.lineCount} line{request.lineCount === 1 ? '' : 's'} · {request.attachmentCount}{' '}
            file{request.attachmentCount === 1 ? '' : 's'}
          </Caption1>
          {request.lastAttemptFailed && (
            <Caption1 className={styles.failedNote}>
              Not created in D365 — open to see why and approve again
            </Caption1>
          )}
        </div>
      ),
    },
    {
      key: 'summary',
      header: 'What it is for',
      priority: 95,
      // The card's counterpart to `request` above: the title and size of the
      // request, minus the number its heading already carries.
      cardOnly: true,
      render: (request) => (
        <div className={styles.identity}>
          <Body1>{request.title}</Body1>
          <Caption1 className={styles.muted}>
            {request.lineCount} line{request.lineCount === 1 ? '' : 's'} · {request.attachmentCount}{' '}
            file{request.attachmentCount === 1 ? '' : 's'}
          </Caption1>
          {request.lastAttemptFailed && (
            <Caption1 className={styles.failedNote}>
              Not created in D365 — open to see why and approve again
            </Caption1>
          )}
        </div>
      ),
    },
    { key: 'requester', header: 'Requester', priority: 60, render: (r) => r.requesterName },
    {
      key: 'vendor',
      header: 'Vendor',
      priority: 50,
      render: (r) => vendorLabel(vendorNames, r),
    },
    { key: 'company', header: 'Company', priority: 40, render: (r) => r.companyCode ?? '—' },
    { key: 'type', header: 'Type', priority: 30, render: (r) => purchaseTypeLabel(r.purchaseType) },
    {
      key: 'total',
      header: 'Total',
      numeric: true,
      priority: 80,
      render: (r) => `${r.currencyCode ?? ''} ${formatTotal(r.grandTotal, r.currencyCode ?? '')}`,
    },
    {
      key: 'waiting',
      header: 'Waiting',
      priority: 70,
      render: (r) => <WaitingBadge since={r.updatedAt} />,
    },
  ];

  return (
    <div className={mergeClasses(layout.stack, responsive.touchTargets)}>
      <PageHeader
        title="Reviewer queue"
        description={
          state.phase === 'ready' && requests.length > 0
            ? `${String(actionableCount)} to review — this queue is shared. Anyone in the review group can action any request here.${
                ownCount > 0
                  ? ` ${String(ownCount)} of the requests here ${ownCount === 1 ? 'is' : 'are'} your own — another reviewer has to decide ${ownCount === 1 ? 'it' : 'them'}.`
                  : ''
              }`
            : undefined
        }
      />

      {state.phase === 'loading' && (
        // Two-second rule: this is a network call on the render path.
        <Spinner label="Loading the reviewer queue…" labelPosition="after" />
      )}

      {state.phase === 'error' &&
        (notAReviewer ? (
          <EmptyState
            variant="no-permission"
            title="You do not have access to the reviewer queue"
            body="Only the review group can see requests waiting for a decision. Ask your D365 administrator if you should be in it."
          />
        ) : (
          <ApiErrorMessage
            error={state.error}
            title="Could not load the reviewer queue"
            onRetry={() => void reload()}
          />
        ))}

      {/* #46, wireframe 05.2 (V7) note 2 — an approved request whose D365 step
          failed stays visible. This section is how any reviewer finds one
          later, not only whoever happened to press Approve. */}
      {failures.length > 0 && (
        <div className={layout.stack}>
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>
                {failures.length} approved request{failures.length === 1 ? '' : 's'} could not be
                created in D365.
              </MessageBarTitle>
              The approvals stand — only the D365 step failed. Open one to see what D365 said and
              retry, send it back, or notify the requester.
            </MessageBarBody>
          </MessageBar>
          <ul className={styles.failureList}>
            {failures.map((failure) => (
              <li
                key={failure.id}
                className={mergeClasses(surfaces.card, styles.failureRow, responsive.singleColumn)}
              >
                <div className={styles.identity}>
                  <div className={styles.failureIdentity}>
                    <Body1Strong>PR-{failure.requestNo}</Body1Strong>
                    <Body1>{failure.title}</Body1>
                  </div>
                  <Caption1 className={styles.muted}>
                    {failure.requesterName} · {failure.attemptCount} attempt
                    {failure.attemptCount === 1 ? '' : 's'} · last{' '}
                    {formatDateTime(failure.lastAttemptAt)}
                  </Caption1>
                </div>
                {/* Secondary, like every other row action: the page's emphasis
                    is not spent once per failed request. */}
                <Button
                  appearance="secondary"
                  aria-label={`Resolve the failed PR creation for PR-${String(failure.requestNo)}`}
                  onClick={() => setSelected({ kind: 'failure', id: failure.id })}
                >
                  Resolve
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Wireframe V1 — search box + company filter, directly above the table,
          and E1 note 3: they stay visible when the search matches nothing, so
          there is always a way back. */}
      {showFilters && (
        <FilterBar
          search={{
            label: 'Search',
            value: searchInput,
            onChange: setSearchInput,
            placeholder: 'Search requester, vendor or request no',
          }}
          activeCount={activeFilterCount}
          onClear={clearFilters}
          busy={busy}
        >
          <Field label="Company">
            <Dropdown
              selectedOptions={[filters.company]}
              value={filters.company || 'All'}
              onOptionSelect={(_, data) =>
                setFilters((f) => ({ ...f, company: data.optionValue ?? '' }))
              }
            >
              <Option value="">All</Option>
              {companies.map((c) => (
                <Option key={c} value={c}>
                  {c}
                </Option>
              ))}
            </Dropdown>
          </Field>
        </FilterBar>
      )}

      {/* V1a — an empty queue is the DESIRED state, so it gets `all-clear` and
          no action: there is nothing to do, and offering a button implies
          otherwise. */}
      {genuinelyEmpty && (
        <EmptyState
          variant="all-clear"
          title="Nothing waiting for review"
          body="New requests appear here as soon as they are submitted."
        />
      )}

      {/* E1 — a filter matching nothing is not an empty queue. The way out is
          the `Clear filters` control in the bar above, which is why this
          variant carries no action of its own (two identical buttons would
          only make the reviewer choose between them). */}
      {hasNoMatches && (
        <EmptyState
          variant="no-results"
          title="No requests match your search or filters."
          body="Clear the search or the company filter above to see everything waiting for review."
        />
      )}

      {state.phase === 'ready' && requests.length > 0 && (
        <DataTable
          caption="Reviewer queue"
          columns={columns}
          rows={requests}
          rowKey={(request) => request.id}
          cardTitle={(request) => `PR-${String(request.requestNo)}`}
          rowActions={renderRowActions}
        />
      )}
    </div>
  );
}
